import React, {
    lazy,
    Suspense,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react"
import { Subject } from "rxjs"
import { Navigate, Outlet, Route, Routes } from "react-router-dom"

import Grid from "@material-ui/core/Grid"
import Paper from "@material-ui/core/Paper"
import { makeStyles, styled } from "@material-ui/core/styles"
import { Theme } from "@material-ui/core"

import LoadingOverlay from "../../components/LoadingOverlay"
import Logo from "../../components/Logo"
import FlexBox from "../../components/FlexBox"
import useLogin from "../../hooks/useLogin"
import { AuthContext } from "../../context/auth"

const PromptPassword = lazy(
    () => import(/* webpackChunkName: "PromptPassword" */ "./PromptPassword"),
)
const UpdateTemporaryPassword = lazy(
    () =>
        import(
            /* webpackChunkName: "UpdateTemporaryPassword" */ "./UpdateTemporaryPassword"
        ),
)
const FindPasswordPanel = lazy(
    () =>
        import(
            /* webpackChunkName: "FindPasswordPanel" */ "./FindPasswordPanel"
        ),
)
const ResendConfirmPanel = lazy(
    () =>
        import(
            /* webpackChunkName: "ResendConfirmPanel" */ "./ResendConfirmPanel"
        ),
)
const LoginPanel = lazy(
    () => import(/* webpackChunkName: "LoginPanel" */ "./LoginPanel"),
)
const SignUpPanel = lazy(
    () => import(/* webpackChunkName: "SignUpPanel" */ "./SignUpPanel"),
)

const StyledPaper = styled(Paper)({
    backgroundColor: "#ffffff",
    margin: "16px 0",
    padding: "49px 45px 38px 45px",
    width: "540px",
    maxWidth: "100%",
    height: "auto",
    overflow: "hidden",
    boxSizing: "border-box",
})

const useStyles = makeStyles(({ palette }: Theme) => ({
    root: {
        alignItems: "center",
        backgroundColor: palette.background.default,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        width: "100%",
        minHeight: "100%",
    },
    wrapper: {
        position: "absolute",
        top: 0,
        bottom: 0,
        width: "100%",
    },
    paperWrapper: {
        maxWidth: "80%",
    },
}))

const GridContainer = styled((props) => (
    <Grid
        container
        direction="column"
        justifyContent="space-between"
        spacing={2}
        wrap="nowrap"
        {...props}
    />
))({
    height: "100%",
})

const LogoContainer = styled((props) => (
    <Grid container item justifyContent="center" {...props} />
))({
    marginBottom: "52px !important",
})

interface IContainer {
    promptUpdateTemporaryPassword: boolean | undefined
    promptResetPassword: boolean | undefined
    doLogin: (email: string, password: string) => void
}

function Container({
    promptUpdateTemporaryPassword,
    promptResetPassword,
    doLogin,
}: IContainer) {
    const classes = useStyles()

    const [message$] = useState(
        new Subject<{ email: string; password: string }>(),
    )

    useEffect(() => {
        const subscription = message$.subscribe(({ email, password }) =>
            doLogin(email, password),
        )

        return () => subscription.unsubscribe()
    }, [message$, doLogin])

    useEffect(() => {
        const electronUrl = "http://localhost:22582"
        window.parent.postMessage("ready", electronUrl)
        const receiveMessage = (e: MessageEvent) => {
            if (e.origin !== electronUrl) {
                return
            }

            const { email, password } = e.data
            message$.next({ email, password })
        }

        window.addEventListener("message", receiveMessage, false)

        return () => {
            window.removeEventListener("message", receiveMessage)
        }
    }, [message$])

    const LoginPageContainer = () => {
        return (
            <div className={classes.wrapper}>
                <div className={classes.root}>
                    <FlexBox
                        className={classes.paperWrapper}
                        flexGrow="1"
                        flexDirection="column"
                        justifyContent="space-around"
                    >
                        <StyledPaper>
                            <GridContainer spacing={0}>
                                <LogoContainer>
                                    <Logo />
                                </LogoContainer>
                                <Outlet />
                            </GridContainer>
                        </StyledPaper>
                    </FlexBox>
                    <FlexBox
                        flexBasis="84px"
                        color="#949495"
                        fontSize="14px"
                        alignItems="center"
                    >
                        {`©
        ${new Date().getFullYear()}
    Ortery Technologies, Inc. All Rights Reserved
    `}
                    </FlexBox>
                </div>
            </div>
        )
    }
    return (
        <Suspense
            fallback={
                <div
                    style={{
                        position: "relative",
                        zIndex: 0,
                        width: "100%",
                        height: "100%",
                    }}
                >
                    <LoadingOverlay />
                </div>
            }
        >
            <Routes>
                <Route path="/" element={<LoginPageContainer />}>
                    {promptResetPassword && (
                        <Route
                            path="/forgot-password"
                            element={<PromptPassword />}
                        />
                    )}
                    {promptUpdateTemporaryPassword && (
                        // 後台建立帳號後 輸入暫時密碼頁面
                        <Route
                            path="/login"
                            element={<UpdateTemporaryPassword />}
                        />
                    )}
                    <Route
                        path="/forgot-password"
                        element={<FindPasswordPanel />}
                    />
                    <Route
                        path="/resend-confirmation"
                        element={<ResendConfirmPanel />}
                    />
                    <Route path="/login" element={<LoginPanel />} />
                    <Route path="/signup" element={<SignUpPanel />} />
                </Route>
            </Routes>
        </Suspense>
    )
}

export default function LoginPage() {
    const login = useLogin()
    const {
        isValidUserChecked,
        checkValidUser$,
        getIdToken,
        refreshUserAbilities,
    } = useContext(AuthContext)
    const { doLogin, promptResetPassword, promptUpdateTemporaryPassword } =
        useContext(AuthContext)

    useEffect(() => {
        if (!login) return
        refreshUserAbilities()
        const sub = getIdToken().subscribe((idtoken) => {
            checkValidUser$.next({ idtoken })
        })
        return () => sub.unsubscribe()
    }, [checkValidUser$, getIdToken, login, refreshUserAbilities])

    const ContainerMemory = useMemo(
        () => (
            <Container
                doLogin={doLogin}
                promptResetPassword={promptResetPassword}
                promptUpdateTemporaryPassword={promptUpdateTemporaryPassword}
            />
        ),

        [doLogin, promptResetPassword, promptUpdateTemporaryPassword],
    )
    return login && isValidUserChecked ? (
        <Navigate to={"/@my-drive"} />
    ) : (
        ContainerMemory
    )
}
